import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
} from "../../components/Common/AppOverlay";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import company from "../../assets/images/auvenir-logo-dark.svg";
import piersLogo from "../../assets/images/landing/piers-logo-light.png";
import loginHeroBackground from "../../assets/images/login-background.png";
import { postLoginRequest } from "../../helpers/backend_helper";
import { BASE_URL } from "../../helpers/url_helper";
import { showLoading } from "react-global-loading";
import { ErrorNotify } from "../../shared/Alert";
import "./Login.scss";
import { onboardingDismissedSessionKey } from "../../config/onboardingTours";

const entraErrorMessage = (code) => {
  const messages = {
    microsoft_sign_in_unavailable:
      "Microsoft sign-in is not available in this PIERS environment.",
    corporate_sign_in_unavailable:
      "Microsoft sign-in is not available for this PIERS account. Contact your PIERS administrator.",
    microsoft_callback_cookie_failed:
      "The Microsoft sign-in callback could not be verified. Start a new sign-in and try again.",
    microsoft_callback_nonce_failed:
      "The Microsoft sign-in response expired or could not be verified. Start a new sign-in and try again.",
    microsoft_identity_validation_failed:
      "Microsoft signed you in, but PIERS could not validate the identity response. Contact your PIERS administrator.",
    microsoft_code_redemption_failed:
      "Microsoft signed you in, but PIERS could not complete the secure callback. Please try again.",
    invalid_external_identity:
      "Microsoft signed you in, but the required Entra identity claims were unavailable. Contact your PIERS administrator.",
    firm_unavailable:
      "Your firm does not have active PIERS access. Contact your administrator.",
    firm_authentication_not_enabled:
      "Microsoft sign-in is not enabled for your firm. Contact your administrator.",
    mapped_user_unavailable:
      "Your PIERS account is unavailable. Contact your administrator.",
    identity_not_preprovisioned:
      "Your Microsoft identity has not been provisioned in PIERS. Contact your PIERS administrator.",
    authentication_transaction_expired_or_replayed:
      "This sign-in request has expired or was already used. Please try again.",
    authentication_transaction_replayed:
      "This sign-in request was already used. Please try again.",
  };
  return code
    ? messages[code] ||
        "Microsoft sign-in could not be completed. Please try again."
    : "";
};

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminBypass = location.pathname.toLowerCase() === "/login";
  const query = new URLSearchParams(location.search);
  const entraError = query.get("entraError");
  const signedOut = query.get("signedOut") === "true";
  const sessionExpired = query.get("sessionExpired") === "true";
  const microsoftSignOutIncomplete = query.get("microsoft") === "incomplete";
  const [loading, setLoading] = useState(false);
  const [loginerror, setLoginError] = useState(entraErrorMessage(entraError));
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    sessionStorage.removeItem(onboardingDismissedSessionKey);
  }, []);

  const startMicrosoftSignIn = () => {
    const returnUrl = `${window.location.origin}/piers/landing`;
    window.location.assign(
      `${BASE_URL}/Authenticate/entra/login?returnUrl=${encodeURIComponent(
        returnUrl
      )}`
    );
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { username: "", password: "" },
    validationSchema: Yup.object({
      username: isAdminBypass
        ? Yup.string()
            .email("Please enter a valid email")
            .required("Please enter your email")
        : Yup.string(),
      password: isAdminBypass
        ? Yup.string().required("Please enter your password")
        : Yup.string(),
    }),
    onSubmit: async (values) => {
      setLoginError("");
      setLoading(true);
      showLoading(true);

      if (!isAdminBypass) {
        startMicrosoftSignIn();
        return;
      }

      try {
        const response = await postLoginRequest(values);
        if (response.status === true) navigate("/twostepverification");
        else setLoginError(response.message || "Invalid username/password");
      } catch {
        ErrorNotify("Server error. Please try again.");
      } finally {
        setLoading(false);
        showLoading(false);
      }
    },
  });

  return (
    <main
      className="login-design"
      style={{ backgroundImage: `url(${loginHeroBackground})` }}
    >
      <section
        className="login-design__brand"
        aria-label="PIERS inspection platform"
      >
        <div className="login-design__brand-content">
          <img
            src={company}
            alt="Streamworks Tech"
            className="login-design__company-logo"
          />
          <div className="login-design__brand-copy">
            <img
              src={piersLogo}
              alt="PIERS — Practice Inspection & Engagement Review System"
              className="login-design__piers-logo"
            />
          </div>
          <div className="login-design__brand-values">
            <div>
              <i className="bx bx-search-alt-2" />
              <strong>Inspect</strong>
              <small>
                Plan and perform
                <br />
                engagement inspections
              </small>
            </div>
            <div>
              <i className="bx bx-task" />
              <strong>Review</strong>
              <small>
                Evaluate findings
                <br />
                with clarity and consistency
              </small>
            </div>
            <div>
              <i className="bx bx-shield-quarter" />
              <strong>Assure</strong>
              <small>Mitigate risk and maintain quality standards</small>
            </div>
            <div>
              <i className="bx bx-line-chart" />
              <strong>Improve</strong>
              <small>Drive insights and continuous improvement</small>
            </div>
          </div>
        </div>
      </section>
      <section className="login-design__sign-in">
        <div className="login-design__card">
          <div className="login-design__security-mark">
            <i
              className={`bx ${
                isAdminBypass ? "bx-lock-alt" : "bx-shield-quarter"
              }`}
            />
          </div>
          <header>
            <h1>
              {isAdminBypass ? "Application administrator" : "Welcome to PIERS"}
            </h1>
            <p>
              {isAdminBypass
                ? "Temporary recovery sign-in for the PIERS super administrator"
                : "Sign in to continue"}
            </p>
          </header>
          {signedOut && (
            <Alert
              color={microsoftSignOutIncomplete ? "warning" : "success"}
              className="login-design__signed-out"
              role="status"
            >
              <i
                className={`bx ${
                  microsoftSignOutIncomplete
                    ? "bx-error-circle"
                    : "bx-check-circle"
                }`}
                aria-hidden="true"
              />
              <span>
                <strong>You’ve been signed out of PIERS.</strong>
                {microsoftSignOutIncomplete && (
                  <small>
                    Microsoft sign-out could not be completed. Your Microsoft
                    session may still be active.
                  </small>
                )}
              </span>
            </Alert>
          )}
          {sessionExpired && (
            <Alert
              color="warning"
              className="login-design__signed-out"
              role="status"
            >
              <i className="bx bx-time-five" aria-hidden="true" />
              <span>
                <strong>Your PIERS session expired due to inactivity.</strong>
                <small>Please sign in again to continue.</small>
              </span>
            </Alert>
          )}
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              validation.handleSubmit();
            }}
            noValidate
          >
            {loginerror && (
              <Alert color="danger" className="login-design__error">
                {loginerror}
              </Alert>
            )}
            {isAdminBypass && (
              <div className="login-design__field">
                <Label for="login-username">Email</Label>
                <div className="login-design__input">
                  <i className="bx bx-user" />
                  <Input
                    id="login-username"
                    name="username"
                    placeholder="Enter your PIERS email"
                    type="email"
                    autoComplete="username"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.username}
                    invalid={Boolean(
                      validation.touched.username && validation.errors.username
                    )}
                  />
                </div>
                {validation.touched.username && validation.errors.username && (
                  <FormFeedback className="d-block">
                    {validation.errors.username}
                  </FormFeedback>
                )}
              </div>
            )}
            {isAdminBypass && (
              <div className="login-design__field">
                <Label for="login-password">Password</Label>
                <div className="login-design__input">
                  <i className="bx bx-lock-alt" />
                  <Input
                    id="login-password"
                    name="password"
                    placeholder="Enter your PIERS password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.password}
                    invalid={Boolean(
                      validation.touched.password && validation.errors.password
                    )}
                  />
                  <button
                    type="button"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <i
                      className={`bx ${showPassword ? "bx-show" : "bx-hide"}`}
                    />
                  </button>
                </div>
                {validation.touched.password && validation.errors.password && (
                  <FormFeedback className="d-block">
                    {validation.errors.password}
                  </FormFeedback>
                )}
              </div>
            )}
            <Button
              className="login-design__submit"
              color="primary"
              disabled={loading}
              type="submit"
            >
              {loading
                ? "Please wait..."
                : isAdminBypass
                ? "Continue"
                : "Sign in to PIERS"}
              <i
                className={`bx ${
                  isAdminBypass ? "bx-right-arrow-alt" : "bx-shield-quarter"
                }`}
              />
            </Button>
          </Form>
          {!isAdminBypass && (
            <section className="login-design__enterprise">
              <p className="login-design__invite">
                Access to PIERS is invitation only. Contact your Firm
                Administrator if you need help signing in.
              </p>
            </section>
          )}
          <footer className="login-design__trust">
            <div>
              <i className="bx bx-shield-quarter" />
              <span>
                <b>Secure &amp; Trusted</b>
                <small>Enterprise grade security</small>
              </span>
            </div>
            <div>
              <i className="bx bx-lock-alt" />
              <span>
                <b>Data Protected</b>
                <small>Your data is safe with us</small>
              </span>
            </div>
            <div>
              <i className="bx bx-check-shield" />
              <span>
                <b>Always Available</b>
                <small>Reliable platform you can count on</small>
              </span>
            </div>
          </footer>
          <p className="login-design__copyright">
            © {new Date().getFullYear()} Streamworks Tech. All rights reserved.
            <br />v 1.0.0.1
          </p>
        </div>
      </section>
    </main>
  );
};

export default Login;
